import { useDispatch, useSelector } from 'react-redux';
import { Router, WifiLock } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { ROUTES } from '@app/router/constants';
import { useNavigate } from '@app/hooks';
import { getFiberServiceSubType, resetSubTypeUpdateStatus, selectNetwork } from '@app/redux';

import { OntMode } from '@mfe/shared/schema-types';

import { NetworkManagementCard } from './network-management-card';
import { OntModeCard } from './ont-mode-card';

type NetworkManagementCardContainerProps = {
  canFetchOntMode: boolean;
  enableFiber: boolean;
  enableBridgeMode: boolean;
  hasFiber: boolean;
};

export const NetworkManagementCardContainer = ({
  canFetchOntMode,
  enableFiber,
  enableBridgeMode,
  hasFiber,
}: NetworkManagementCardContainerProps) => {
  const { t } = useTranslation('Network');
  const dispatch = useDispatch();
  const { goTo } = useNavigate();
  const { ontModeLoading, ontMode } = useSelector(selectNetwork);

  const onNetworkManagementClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    goTo(ROUTES.NETWORK_MANAGEMENT);
  };

  const onSwitchToBridgeModeClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    dispatch(resetSubTypeUpdateStatus());
    goTo(ROUTES.NETWORK_BRIDGE);
  };

  const onSwitchToWifiModeClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    dispatch(resetSubTypeUpdateStatus());
    goTo(ROUTES.NETWORK_WIFI);
  };

  const onErrorClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (canFetchOntMode) {
      dispatch(getFiberServiceSubType());
    }
  };

  if (!enableFiber || !hasFiber) {
    return null;
  }

  if (!enableBridgeMode) {
    return (
      <NetworkManagementCard
        icon={WifiLock}
        title={t('networkManagement.cardTitle')}
        buttonLabel={t('networkManagement.wifiCardLink')}
        onClick={onNetworkManagementClick}
      />
    );
  }

  if (ontModeLoading) {
    return (
      <>
        <NetworkManagementCard icon={WifiLock} title={t('networkManagement.cardTitle')} loading={true} />
        <NetworkManagementCard icon={Router} title={t('networkManagement.currentMode')} loading={true} />
      </>
    );
  }

  return (
    <>
      {ontMode === OntMode.WiFi && (
        <NetworkManagementCard
          icon={WifiLock}
          title={t('networkManagement.cardTitle')}
          buttonLabel={t('networkManagement.wifiCardLink')}
          onClick={onNetworkManagementClick}
        />
      )}
      <OntModeCard
        ontMode={ontMode}
        onErrorClick={onErrorClick}
        onSwitchToBridgeModeClick={onSwitchToBridgeModeClick}
        onSwitchToWifiModeClick={onSwitchToWifiModeClick}
      />
    </>
  );
};
